import React, { useEffect, useState } from 'react'
import {
	Box,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'
import { IconSeeker } from '@ui-seeker/core'
import { dsColorNew, dataColors, shades } from '@palette'

/**
 * Sprint 2 · point 12 — Revenue by Pricelist Group.
 *
 * Groups come from Admin > Pricelist Groups (Direct / Broker / Prepaid /
 * Corporate + custom). Each group row can be expanded to see its
 * pricelist_codes and the top-5 partners per code — this is the drill
 * Thibault asked for: "por partner cuánto vendo, y en qué tarifa".
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtMoney = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
	}).format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

const GROUP_COLORS = {
	direct:    dsColorNew.webBlue500,
	broker:    dsColorNew.cyan,
	prepaid:   dataColors.violet500,
	corporate: dataColors.emerald500,
	unassigned:dsColorNew.textSubtle,
}

export default function PricelistBreakdown({
	from, to, offices = [], categories = [], compare = 'py',
}) {
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [openGroup, setOpen]  = useState(null)
	const [drill, setDrill]     = useState({}) // {group_code: {loading, rows, error}}

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null); setOpen(null); setDrill({})
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (compare && compare !== 'none') qs.set('compare', compare)
		fetch(`/api/my-business/revenue/by-pricelist-group?${qs}`,
			  { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), compare])

	function toggleGroup(groupCode) {
		if (openGroup === groupCode) { setOpen(null); return }
		setOpen(groupCode)
		if (drill[groupCode]) return
		setDrill(d => ({ ...d, [groupCode]: { loading: true } }))
		const qs = new URLSearchParams({ from, to, group_code: groupCode })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		fetch(`/api/my-business/revenue/by-pricelist-code?${qs}`,
			  { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(j => setDrill(d => ({ ...d, [groupCode]: { rows: j.codes || [] } })))
			.catch(e => setDrill(d => ({ ...d, [groupCode]: { error: e.message } })))
	}

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Pricelist breakdown unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={5} columns={7}/>
	const groups = data.groups || []
	if (!groups.length) return (
		<Paper sx={{ p: 3 }}>
			<AppEmptyLine>No revenue in this range.</AppEmptyLine>
		</Paper>
	)
	const showPY = groups[0]?.revenue_py !== undefined

	return (
		<Paper sx={{ p: 2.5 }}>
			<AppCardTitle icon="tags" title={`Revenue by Pricelist Group · ${fmtMoney(data.total_revenue)} total`} sx={{ mb: 1.5 }}/>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell width={40}/>
							<TableCell>Group</TableCell>
							<TableCell align="right" width={100}>Bookings</TableCell>
							<TableCell align="right" width={140}>Revenue</TableCell>
							<TableCell align="right" width={90}>Share</TableCell>
							<TableCell align="right" width={110}>RPD</TableCell>
							{showPY && <TableCell align="right" width={130}>PY</TableCell>}
							{showPY && <TableCell align="right" width={90}>Δ%</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{groups.map(g => {
							const color = GROUP_COLORS[g.group_code] || shades.grey[700]
							const isOpen = openGroup === g.group_code
							const d = drill[g.group_code]
							const delta = g.delta_pct
							const deltaColor = delta == null ? 'default'
								: delta > 0 ? 'success'
								: delta < 0 ? 'error' : 'default'
							return (
								<React.Fragment key={g.group_code}>
									<TableRow hover
									          sx={{ '& > td': { borderBottom: 'unset' },
									                cursor: 'pointer',
									                bgcolor: g.group_code === 'unassigned'
									                          ? 'warning.50' : undefined }}
									          onClick={() => toggleGroup(g.group_code)}>
										<TableCell>
											<IconButton size="small"
											            onClick={e => { e.stopPropagation(); toggleGroup(g.group_code) }}>
												<IconSeeker icon={isOpen ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: 12, color: dsColorNew.textMuted }}/>
											</IconButton>
										</TableCell>
										<TableCell>
											<Chip size="small" label={g.group_name}
											      sx={{ bgcolor: color, color: dsColorNew.white,
											            fontWeight: 600, fontSize: 12 }}/>
										</TableCell>
										<TableCell align="right">{fmtNum(g.bookings)}</TableCell>
										<TableCell align="right"><b>{fmtMoney(g.revenue)}</b></TableCell>
										<TableCell align="right">{fmtPct(g.share_pct)}</TableCell>
										<TableCell align="right">{fmtMoney(g.rpd)}</TableCell>
										{showPY && (
											<TableCell align="right">{fmtMoney(g.revenue_py || 0)}</TableCell>
										)}
										{showPY && (
											<TableCell align="right">
												{delta == null ? <em>—</em> : (
													<Chip size="small" color={deltaColor}
													      variant="filled"
													      label={`${delta > 0 ? '+' : ''}${delta}%`}
													      sx={{ fontWeight: 700, fontSize: 12 }}/>
												)}
											</TableCell>
										)}
									</TableRow>
									<TableRow>
										<TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : 'unset' }}
										           colSpan={showPY ? 8 : 6}>
											<Collapse in={isOpen} timeout="auto" unmountOnExit>
												<Box sx={{ py: 2, px: 3 }}>
													{d?.loading && (
														<Box display="flex" gap={1} alignItems="center">
															<CircularProgress size={14}/>
															<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>Loading codes…</Typography>
														</Box>
													)}
													{d?.error && (
														<AppNote severity="error">Drill unreachable: {d.error}</AppNote>
													)}
													{d?.rows && d.rows.length === 0 && (
														<AppEmptyLine>No pricelist codes for this group in the range.</AppEmptyLine>
													)}
													{d?.rows && d.rows.length > 0 && (
														<Table size="small">
															<TableHead>
																<TableRow>
																	<TableCell>Pricelist code</TableCell>
																	<TableCell align="right">Bookings</TableCell>
																	<TableCell align="right">Revenue</TableCell>
																	<TableCell align="right">Share</TableCell>
																	<TableCell>Top partners</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																{d.rows.map(c => (
																	<TableRow key={c.pricelist_code}>
																		<TableCell>
																			<Typography component="span"
																			            sx={{ fontWeight: 600 }}>
																				{c.pricelist_code}
																			</Typography>
																		</TableCell>
																		<TableCell align="right">{fmtNum(c.bookings)}</TableCell>
																		<TableCell align="right">{fmtMoney(c.revenue)}</TableCell>
																		<TableCell align="right">{fmtPct(c.share_pct)}</TableCell>
																		<TableCell>
																			<Box display="flex" flexWrap="wrap" gap={0.5}>
																				{c.by_partner.map((p, i) => (
																					<Chip key={i} size="small"
																					      variant="outlined"
																					      label={`${p.partner} · ${fmtMoney(p.revenue)}`}
																					      sx={{ fontSize: 11 }}/>
																				))}
																			</Box>
																		</TableCell>
																	</TableRow>
																))}
															</TableBody>
														</Table>
													)}
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	)
}
